import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { FileText, Printer, Download } from "lucide-react";
import { getStatusBadgeStyle } from "./utils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StatusBadge = ({ status }) => (
  <Badge className={`${getStatusBadgeStyle(status)} font-medium`}>
    {status}
  </Badge>
);

export const InvoicesTab = ({ invoices }) => {
  const [selectedInvoice, setSelectedInvoice] = useState(null);

  const handleViewInvoice = async (bookingId) => {
    try {
      const response = await axios.get(`${API}/invoice/${bookingId}`);
      setSelectedInvoice(response.data);
    } catch (error) {
      toast.error("Failed to load invoice");
    }
  };

  const handleDownloadPdf = async (bookingId, invoiceNumber) => {
    try {
      const response = await axios.get(`${API}/invoice/${bookingId}/pdf`, {
        responseType: "blob",
      });
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${invoiceNumber || "invoice"}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error("Failed to download PDF");
    }
  };

  return (
    <>
      <Card className="bg-white border-[#E2E8E0]">
        <CardHeader>
          <CardTitle className="text-[#1A2E12]">Invoices & Receipts</CardTitle>
        </CardHeader>
        <CardContent>
          {invoices.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-12 h-12 text-[#E2E8E0] mx-auto mb-4" />
              <p className="text-[#5C6B57] mb-4">No invoices yet</p>
              <p className="text-sm text-[#5C6B57]">
                Invoices will appear here after your bookings are paid
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {invoices.map((invoice) => (
                <div
                  key={invoice.invoice_number}
                  className="border border-[#E2E8E0] rounded-xl p-4 hover:shadow-md transition-shadow"
                >
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <FileText className="w-5 h-5 text-[#2D5016]" />
                        <h3 className="font-semibold text-[#1A2E12]">
                          {invoice.invoice_number}
                        </h3>
                        <StatusBadge status={invoice.payment_status} />
                      </div>
                      <div className="flex flex-wrap gap-4 text-sm text-[#5C6B57]">
                        <span>{invoice.service_name}</span>
                        <span>•</span>
                        <span>
                          {new Date(invoice.invoice_date).toLocaleDateString(
                            "en-GB"
                          )}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        {invoice.vat_amount > 0 && (
                          <p className="text-xs text-[#5C6B57]">
                            Net: £{invoice.subtotal.toFixed(2)} + VAT: £
                            {invoice.vat_amount.toFixed(2)}
                          </p>
                        )}
                        <p className="text-xl font-bold text-[#2D5016]">
                          £{invoice.total.toFixed(2)}
                        </p>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleViewInvoice(invoice.booking_id)}
                        className="border-[#2D5016] text-[#2D5016]"
                        data-testid={`view-invoice-${invoice.invoice_number}`}
                        aria-label="View invoice"
                      >
                        <Printer className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDownloadPdf(invoice.booking_id, invoice.invoice_number)}
                        className="border-[#2D5016] text-[#2D5016]"
                        data-testid={`download-invoice-${invoice.invoice_number}`}
                        aria-label="Download PDF invoice"
                      >
                        <Download className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {selectedInvoice && (
        <Dialog
          open={!!selectedInvoice}
          onOpenChange={() => setSelectedInvoice(null)}
        >
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Invoice {selectedInvoice.invoice_number}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-6 mt-4" data-testid="invoice-detail">
              <div className="border-b border-[#E2E8E0] pb-4">
                <h3 className="text-xl font-bold text-[#1A2E12]">
                  {selectedInvoice.company.name}
                </h3>
                <p className="text-sm text-[#5C6B57]">
                  {selectedInvoice.company.address}
                </p>
                <p className="text-sm text-[#5C6B57]">
                  {selectedInvoice.company.email} |{" "}
                  {selectedInvoice.company.phone}
                </p>
                {selectedInvoice.company.vat_registered && (
                  <p className="text-sm text-[#5C6B57]">
                    VAT No: {selectedInvoice.company.vat_number}
                  </p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-[#5C6B57]">Invoice Number</p>
                  <p className="font-medium text-[#1A2E12]">
                    {selectedInvoice.invoice_number}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-[#5C6B57]">Invoice Date</p>
                  <p className="font-medium text-[#1A2E12]">
                    {new Date(selectedInvoice.invoice_date).toLocaleDateString(
                      "en-GB"
                    )}
                  </p>
                </div>
              </div>

              <div className="bg-[#F9FAF9] rounded-xl p-4">
                <p className="text-sm font-semibold text-[#2D5016] mb-2">
                  Bill To:
                </p>
                <p className="text-[#1A2E12]">
                  {selectedInvoice.customer.name}
                </p>
                <p className="text-sm text-[#5C6B57]">
                  {selectedInvoice.customer.email}
                </p>
                {selectedInvoice.customer.address && (
                  <p className="text-sm text-[#5C6B57]">
                    {selectedInvoice.customer.address},{" "}
                    {selectedInvoice.customer.postcode}
                  </p>
                )}
              </div>

              <div>
                <table className="w-full text-sm">
                  <thead className="border-b border-[#E2E8E0]">
                    <tr>
                      <th className="text-left py-2 text-[#5C6B57]">
                        Description
                      </th>
                      <th className="text-right py-2 text-[#5C6B57]">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedInvoice.items.map((item) => (
                      <tr
                        key={item.description}
                        className="border-b border-[#E2E8E0]"
                      >
                        <td className="py-3">
                          <p className="font-medium text-[#1A2E12]">
                            {item.description}
                          </p>
                          <p className="text-xs text-[#5C6B57]">
                            {item.details}
                          </p>
                          <p className="text-xs text-[#5C6B57]">
                            {item.date} at {item.time}
                          </p>
                        </td>
                        <td className="py-3 text-right font-medium text-[#1A2E12]">
                          £{item.total.toFixed(2)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="bg-[#E8F5E9] rounded-xl p-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-[#5C6B57]">Subtotal</span>
                  <span className="text-[#1A2E12]">
                    £{selectedInvoice.subtotal.toFixed(2)}
                  </span>
                </div>
                {selectedInvoice.vat_rate > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-[#5C6B57]">
                      VAT ({selectedInvoice.vat_rate}%)
                    </span>
                    <span className="text-[#1A2E12]">
                      £{selectedInvoice.vat_amount.toFixed(2)}
                    </span>
                  </div>
                )}
                <div className="flex justify-between font-bold text-lg border-t border-[#2D5016]/20 pt-2">
                  <span className="text-[#1A2E12]">
                    Total ({selectedInvoice.currency})
                  </span>
                  <span className="text-[#2D5016]">
                    £{selectedInvoice.total.toFixed(2)}
                  </span>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-sm text-[#5C6B57]">Payment Status</span>
                <StatusBadge status={selectedInvoice.payment_status} />
              </div>

              <p className="text-xs text-[#5C6B57] border-t border-[#E2E8E0] pt-4">
                {selectedInvoice.legal_text}
              </p>

              <Button
                onClick={() => window.print()}
                className="w-full bg-[#2D5016] hover:bg-[#1A2E12] rounded-full"
              >
                <Printer className="w-4 h-4 mr-2" />
                Print Invoice
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};
